import { AfterViewInit, Component, ElementRef, Renderer2, ViewChild } from '@angular/core';
import SignaturePad from 'signature_pad';
import { Router } from '@angular/router';
import { ballotListRoute, ballotRoute, dashboardRoute, reviewRoute } from '../route-names';
import { BehaviorSubject } from 'rxjs';
import { BallotService } from '../ballot/ballot.service';
import imageCompression from 'browser-image-compression';
import { BrowserSupportService } from '../browser-support/browser-support.service';
import { MatDialog } from '@angular/material/dialog';
import { BallotConfirmAmComponent } from '../ballot/ballot-confirm-am.component';
import { ExitModalComponent } from '../exit-modal/exit-modal.component';
import { Ballot, PetitionFields } from '../ballot/backend-types';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { maxEmailLength } from '../verify-voter/verify-voter.component';
import { ApiAboutService } from '../Services/api-about.service';
import { DecisionDialogService, DialogType } from '../Services/decision-dialog.service';

@Component({
    selector: 'app-petition',
    templateUrl: './petition.component.html',
    styleUrls: ['./petition.component.scss'],
    standalone: false
})
export class PetitionComponent implements AfterViewInit {
  @ViewChild('canvas') canvas!: ElementRef;
  public signaturePad!: SignaturePad;
  ballot: Ballot | undefined;
  readonly signature = new BehaviorSubject<string>('');
  readonly isEmpty = new BehaviorSubject<boolean>(true);
  width = 0;
  readonly title: string;
  readonly description: string | undefined;
  petitionText: string | undefined;
  petitionForm: FormGroup = new FormGroup({});
  fields: Array<PetitionFields> = [];
  deliveryPristine = false;
  
  constructor(
    private renderer: Renderer2,
    private readonly router: Router,
    private readonly ballotService: BallotService,
    private readonly browserSupportService: BrowserSupportService,
    private readonly dialog: MatDialog,
    private readonly decisionDialogService: DecisionDialogService,
    private readonly fb: FormBuilder,
    private readonly about: ApiAboutService
  ) {
    this.ballot = this.ballotService.ballot;
    this.title = this.ballot?.voteEventData.description ?? $localize`Petition`;
    this.description = this.ballot?.voteEventData.description1;
    this.petitionText = this.ballot?.eventMetadata?.petition.petitionText;
    this.ballot?.eventMetadata?.petition.textPlaceholders.forEach((item)=> {
      this.petitionText = this.petitionText?.replace(`{${item.key}}`, item.value);
    });
    this.fields = this.ballot?.eventMetadata?.petition.fields ?? [];
    for (let field of this.fields) {
      let validators: ValidatorFn[] = [];
      if (field.isMandatory) {
        validators.push(Validators.required);
      }
      if (field.fieldType === 'string') {
        validators.push(Validators.pattern(/^[^{\}\(\)\[\]\+\*\?\^\$\|\\]{1,60}$/));
      }
      if (field.fieldType === 'email') {    
        validators.push(Validators.maxLength(maxEmailLength));
        validators.push(Validators.email);
      }
      if (field.fieldType !== 'lov') {
        this.petitionForm.addControl(field.fieldName, this.fb.control(null, validators));
      } else {
        let lov: any[] = this.ballot?.eventMetadata?.petition.listOfValues.find((lovs)=>lovs.fieldName === field.fieldName)?.fieldValues ?? [];
        validators.push(this.valueInArrayValidator(lov));
        this.petitionForm.addControl(field.fieldName, new FormControl('', validators));
      }
    }
  }

  ngAfterViewInit(): void {
    this.signaturePad = new SignaturePad(this.renderer.selectRootElement(this.canvas['nativeElement']), {
      backgroundColor: '#fff'
    });
    this.onResize();
    this.clear();
    this.width = window.innerWidth;
    this.resetFocus()
  }

  resetFocus() {
    let focusField = document.getElementById('accessibility-button')
    let focusFieldMobile = document.getElementById('accessibility-button-mobile')
  
    if (focusField) {
      focusField.focus()
    }

    if (focusFieldMobile) {
     focusFieldMobile.focus()
    }
  }

  onResize(): void {
    if(window.innerWidth !== this.width){
      var ratio =  Math.max(window.devicePixelRatio || 1, 1);
      this.canvas.nativeElement.width = this.canvas.nativeElement.offsetWidth * ratio;
      this.canvas.nativeElement.height = this.canvas.nativeElement.offsetHeight * ratio;
      this.canvas.nativeElement.getContext('2d').scale(ratio, ratio);
      this.signaturePad.backgroundColor = '#fff';
      if(!this.browserSupportService.resizeCanvasSupported) {
        this.clear();
      }
    }
  }

  clear() {
    this.signaturePad.clear();
    this.isEmpty.next(this.signaturePad.isEmpty());
  }

  undo() {
    let data = this.signaturePad.toData();
    if (data) {
      data.pop();
      this.signaturePad.fromData(data);
    }
    this.isEmpty.next(this.signaturePad.isEmpty());
  }
  
  goToReview() {
    this.router.navigate(reviewRoute);
  }
  
  touchedCanvas() {
      this.isEmpty.next(this.signaturePad.isEmpty());
  }

  async compressImage() {
    const data = this.signaturePad.toDataURL('image/jpeg');
    const imageFile = await imageCompression.getFilefromDataUrl(data, 'Test');
    const compressedImageFile = await imageCompression(imageFile,{ maxSizeMB: 0.05 });
    const compressedFileURL = await imageCompression.getDataUrlFromFile(compressedImageFile);
    //converts Base64URI to Base64
    this.signature.next(compressedFileURL.replace(/-/g, '+').replace(/_/g, '/').replace(/^data:image\/[a-z]+;base64,/, ''));
    return this.signature.getValue();
  }

  async openSubmitModal(event: any) {
    event.preventDefault();
    let compressedImageEncoding = '';
    await this.compressImage().then(image => compressedImageEncoding = image);
    this.signature.next(compressedImageEncoding);
    this.dialog.open(BallotConfirmAmComponent, {
      data: {ballot: this.ballot, signature: compressedImageEncoding}
    })
  }
  
  exit() {
    const dialogRef = this.decisionDialogService.openDialog(
      DialogType.CONFIRM, 
      $localize`Are you sure you want to leave the petition?`,
      $localize`All your selections will be lost.`,
    );
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (this.about.hasDashboard) {
          this.router.navigate(dashboardRoute);
        } else {
          this.router.navigate(ballotListRoute);
        }
      }
    });

  }

  valueInArrayValidator(validValues: string[]): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      const selected = control.value;
      if (!selected) {
        return null;
      }
      return validValues.includes(selected) ? null : { 'invalidSelection': {value: selected} };
    };
  }

  pickerOptionsFor(fieldName: string): string[] {
    return this.ballot?.eventMetadata?.petition.listOfValues.find((lovs)=>lovs.fieldName === fieldName)?.fieldValues ?? [];
  }

  onTooltipClick(event: any) {
    event.preventDefault();
  }

  onBlur(fieldName: string){
    const control = this.petitionForm.controls[fieldName];  
    if (control.pristine) {
      this.deliveryPristine = true;
    } else {
      this.deliveryPristine = false;
    }
  }

}
